import { api } from '@/lib/api'
import type {
  Conversation,
  ConversationPanel,
  WorkspaceModule,
} from '@/lib/api/endpoints/workspaces'
import type { Module } from '@/types/modules'
import { MODULE_ALIASES, getModuleMeta } from '@/types/modules'

const PANEL_KEY_PREFIX = 'module:'
const HIDDEN_MODULE_TYPES = ['timeline', 'calendar']

// Optimistic temp conversations are created client-side with a synthetic
// `conversation-<timestamp>` id and have no server row yet (the real id is
// swapped in by createConversationMutation.onSuccess). Hitting the gateway with
// the temp id produces repeated 404s in the console (#4285). Sibling hooks guard
// the same way (useSessionMessageLoader.ts, chatbox/module-pane-helpers.ts).
const TEMP_CONVERSATION_ID_PREFIX = 'conversation-'

function isTempConversationId(conversationId: string): boolean {
  return conversationId.startsWith(TEMP_CONVERSATION_ID_PREFIX)
}

/**
 * Synthesize a minimal, server-less `Conversation` for a temp/optimistic id so
 * we can return the exact same snapshot shape callers already handle for a
 * brand-new conversation (a valid summary with `modules: []`). Routing it
 * through `mapConversationSummary` guarantees the return type matches the
 * real-id path byte-for-byte — no parallel "empty shape" to drift.
 */
function synthesizeTempConversation(workspaceId: string, conversationId: string): Conversation {
  const now = new Date().toISOString()
  return {
    id: conversationId,
    workspaceId,
    title: '',
    position: 0,
    createdAt: now,
    updatedAt: now,
    version: 0,
  }
}
type NormalizeConversationOptions = {
  includeArchivedModules?: boolean
}

function parseModuleConfig(config: unknown): Record<string, unknown> {
  if (config && typeof config === 'object' && !Array.isArray(config)) {
    return { ...(config as Record<string, unknown>) }
  }
  return {}
}

function extractLegacyModuleMeta(config: Record<string, unknown>) {
  const moduleMeta = {
    objectType: typeof config.__objectType === 'string' ? config.__objectType : undefined,
    objectTypes: Array.isArray(config.__objectTypes)
      ? config.__objectTypes.filter((value): value is string => typeof value === 'string')
      : undefined,
    color: typeof config.__color === 'string' ? config.__color : undefined,
    accentColor: typeof config.__accentColor === 'string' ? config.__accentColor : undefined,
  }

  const cleanConfig = { ...config }
  delete cleanConfig.__objectType
  delete cleanConfig.__objectTypes
  delete cleanConfig.__color
  delete cleanConfig.__accentColor

  return { moduleMeta, cleanConfig }
}

function encodeModuleConfig(module: Module): Record<string, unknown> {
  const config = { ...(module.config ?? {}) } as Record<string, unknown>
  if (module.objectType) config.__objectType = module.objectType
  if (module.objectTypes) config.__objectTypes = module.objectTypes
  if (module.color) config.__color = module.color
  if (module.accentColor) config.__accentColor = module.accentColor
  return config
}

export function mapConversationSummary(
  conversation: Conversation,
  modules: Module[] = [],
) {
  const existingMeta =
    conversation.meta && typeof conversation.meta === 'object' && !Array.isArray(conversation.meta)
      ? conversation.meta
      : {}

  return {
    id: conversation.id,
    name: conversation.title || 'New chat',
    icon: conversation.icon || ':xavior-logo:',
    color: '#3b82f6',
    visibility: 'private',
    createdAt: conversation.createdAt,
    updatedAt: conversation.updatedAt,
    position: conversation.position,
    archivedAt: conversation.archivedAt,
    version: conversation.version,
    modules,
    meta: {
      ...existingMeta,
      modules: {
        version: 2,
        modules,
      },
    },
    _etag: String(conversation.version ?? ''),
  }
}

export const mapConversationSummaryToConversation = mapConversationSummary

function mapWorkspaceModuleToModule(
  workspaceModule: WorkspaceModule,
  conversationId: string,
  panel?: ConversationPanel,
): Module {
  const parsedConfig = parseModuleConfig(workspaceModule.config)
  const { moduleMeta, cleanConfig } = extractLegacyModuleMeta(parsedConfig)

  const module: Module = {
    id: workspaceModule.id,
    conversation_id: workspaceModule.conversationId || conversationId,
    type: (MODULE_ALIASES[workspaceModule.type as keyof typeof MODULE_ALIASES] ?? workspaceModule.type) as any,
    label: workspaceModule.label,
    position: panel?.sortOrder ?? workspaceModule.position ?? 0,
    size: (workspaceModule.size as any) ?? 'normal',
    config: cleanConfig,
    archived: Boolean(workspaceModule.archivedAt),
    objectType: moduleMeta.objectType as any,
    objectTypes: moduleMeta.objectTypes as any,
    color: moduleMeta.color,
    accentColor: moduleMeta.accentColor,
  }

  if (!module.objectType && !module.objectTypes) {
    const meta = getModuleMeta(module.type as any)
    if (meta?.primaryObjectType) module.objectType = meta.primaryObjectType
    if (meta?.objectTypes) module.objectTypes = meta.objectTypes
  }

  return module
}

async function listConversationModules(
  authToken: string | null,
  workspaceId: string,
  conversationId: string,
  options: NormalizeConversationOptions = {},
): Promise<Module[]> {
  // Temp optimistic conversation: no server row, so listing panels would 404.
  if (isTempConversationId(conversationId)) return []

  const [workspaceModules, panels] = await Promise.all([
    api.workspaces.listWorkspaceModules(authToken, workspaceId, { includeArchived: true }),
    api.workspaces.listConversationPanels(authToken, workspaceId, conversationId, { includeArchived: true }),
  ])

  const workspaceModuleById = new Map(workspaceModules.map((item) => [item.id, item]))
  const orderedPanels = [...panels].sort((left, right) => left.sortOrder - right.sortOrder)
  const includeArchivedModules = options.includeArchivedModules === true

  const modules: Module[] = []
  for (const panel of orderedPanels) {
    const workspaceModule = workspaceModuleById.get(panel.moduleId)
    if (!workspaceModule) continue

    const mapped = mapWorkspaceModuleToModule(workspaceModule, conversationId, panel)
    if (!includeArchivedModules && mapped.archived) continue

    const canonicalType = MODULE_ALIASES[mapped.type] ?? mapped.type
    if (HIDDEN_MODULE_TYPES.includes(canonicalType as string)) continue

    modules.push(mapped)
  }

  return modules
}

export async function fetchConversationSnapshot(
  authToken: string | null,
  workspaceId: string,
  conversationId: string,
  options: NormalizeConversationOptions = {},
) {
  // Temp optimistic conversation: skip the getConversation + listConversationPanels
  // server calls (they 404 — there is no server row yet, #4285) and return the
  // same "empty but valid" snapshot callers handle for a brand-new conversation:
  // a synthesized summary with `modules: []`.
  if (isTempConversationId(conversationId)) {
    const mappedConversation = mapConversationSummary(
      synthesizeTempConversation(workspaceId, conversationId),
      [],
    )
    return {
      conversation: mappedConversation,
    }
  }

  const [conversation, modules] = await Promise.all([
    api.workspaces.getConversation(authToken, workspaceId, conversationId),
    listConversationModules(authToken, workspaceId, conversationId, options),
  ])

  const mappedConversation = mapConversationSummary(conversation, modules)
  return {
    conversation: mappedConversation,
  }
}

export async function fetchConversationsSnapshot(
  authToken: string | null,
  workspaceId: string,
  options?: { limit?: number; includeArchived?: boolean },
){
  const conversations = await api.workspaces.listConversations(authToken, workspaceId, {
    limit: options?.limit,
    includeArchived: options?.includeArchived,
  })

  const items = conversations.map((conversation) => mapConversationSummary(conversation, []))
  return {
    conversations: items,
    total: items.length,
  }
}

export async function syncConversationModules(
  authToken: string | null,
  workspaceId: string,
  conversationId: string,
  desiredModules: Module[],
): Promise<void> {
  // Temp optimistic conversation: panels can't persist against a non-existent
  // server id, and every panel call below would 404 (#4285). No-op until the
  // real id is swapped in; the modules are already held in optimistic UI state.
  if (isTempConversationId(conversationId)) return

  const [existingWorkspaceModules, existingPanels] = await Promise.all([
    api.workspaces.listWorkspaceModules(authToken, workspaceId, { includeArchived: true }),
    api.workspaces.listConversationPanels(authToken, workspaceId, conversationId, { includeArchived: true }),
  ])

  const moduleById = new Map(existingWorkspaceModules.map((item) => [item.id, item]))
  const panelByModuleId = new Map(existingPanels.map((item) => [item.moduleId, item]))
  const scopedModules = desiredModules.map((module) => ({ ...module, conversation_id: conversationId }))
  const orderedModules = [...scopedModules].sort((left, right) => (left.position ?? 0) - (right.position ?? 0))
  const desiredModuleIds = new Set(orderedModules.map((item) => item.id))

  for (const module of orderedModules) {
    const canonicalType = MODULE_ALIASES[module.type] ?? module.type
    const isArchived = Boolean(module.archived)
    const rawPosition = Number(module.position)
    const safePosition = Number.isFinite(rawPosition) && rawPosition >= 0 ? Math.floor(rawPosition) : 0
    const basePayload = {
      type: canonicalType,
      label: module.label,
      position: safePosition,
      size: module.size ?? 'normal',
      config: encodeModuleConfig(module),
    }

    const existingWorkspaceModule = moduleById.get(module.id)
    if (existingWorkspaceModule) {
      // Never propagate archived state to workspace modules — panel archival
      // is conversation-scoped, not workspace-scoped. Archiving a workspace
      // module makes it invisible across ALL conversations. (#2039)
      await api.workspaces.updateWorkspaceModule(authToken, workspaceId, module.id, basePayload)
    } else if (!isArchived) {
      await api.workspaces.createWorkspaceModule(authToken, workspaceId, {
        id: module.id,
        ...basePayload,
      })
    }

    const existingPanel = panelByModuleId.get(module.id)
    if (existingPanel) {
      await api.workspaces.updateConversationPanel(
        authToken,
        workspaceId,
        conversationId,
        existingPanel.id,
        {
          title: module.label,
          sortOrder: module.position ?? 0,
          archived: isArchived,
        },
      )
    } else if (!isArchived) {
      await api.workspaces.createConversationPanel(authToken, workspaceId, conversationId, {
        workspaceModuleId: module.id,
        panelKey: `${PANEL_KEY_PREFIX}${module.id}`,
        title: module.label,
        sortOrder: module.position ?? 0,
      })
    }
  }

  for (const panel of existingPanels) {
    if (desiredModuleIds.has(panel.moduleId) || panel.archivedAt) continue
    await api.workspaces.updateConversationPanel(
      authToken,
      workspaceId,
      conversationId,
      panel.id,
      { archived: true },
    )
  }
}
